import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { WasteMapLogo } from './Logo';
import { Loader2 } from 'lucide-react';

export enum LoginState {
  BOOT = 'BOOT',
  RESTORE_SESSION = 'RESTORE_SESSION',
  FETCH_PROFILE = 'FETCH_PROFILE',
  VERIFY_PERMISSIONS = 'VERIFY_PERMISSIONS',
  READY = 'READY',
  ERROR = 'ERROR'
}

interface SplashScreenProps {
  loginState: LoginState;
  isVisible: boolean;
  isDarkMode?: boolean;
}

export const SplashScreen: React.FC<SplashScreenProps> = ({
  loginState,
  isVisible,
  isDarkMode = false
}) => {
  const getStateLabel = () => {
    switch (loginState) {
      case LoginState.BOOT:
        return 'Inicializando subsistemas...';
      case LoginState.RESTORE_SESSION:
        return 'Restaurando sessão segura...';
      case LoginState.FETCH_PROFILE:
        return 'Sincronizando perfil corporativo...';
      case LoginState.VERIFY_PERMISSIONS:
        return 'Verificando privilégios e políticas...';
      case LoginState.READY:
        return 'Ambiente pronto!';
      case LoginState.ERROR:
        return 'Falha crítica na inicialização.';
      default:
        return 'Carregando...';
    }
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          key="splash-screen"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.45, ease: [0.16, 1, 0.3, 1] } }}
          className={`fixed inset-0 z-[99999] flex flex-col items-center justify-center ${
            isDarkMode ? 'bg-slate-950 text-white' : 'bg-white text-slate-900'
          }`}
        >
          <div className="relative flex flex-col items-center max-w-sm px-6 text-center">
            {/* Centered Decorative Accent */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
              className="mb-8"
            >
              <WasteMapLogo className="h-24 w-auto" isDarkMode={isDarkMode} />
            </motion.div>

            {/* Spinner and State Text */}
            <div className="flex flex-col items-center space-y-4">
              <div className="flex items-center gap-3">
                <Loader2 className={`w-5 h-5 animate-spin ${
                  isDarkMode ? 'text-blue-400' : 'text-slate-800'
                }`} />
                <span className={`text-[10px] font-mono uppercase tracking-[0.2em] ${
                  isDarkMode ? 'text-slate-400' : 'text-slate-500'
                }`}>
                  {loginState}
                </span>
              </div>

              {/* Action/Progress label */}
              <motion.p
                key={loginState}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.2 }}
                className={`text-xs font-semibold ${
                  isDarkMode ? 'text-slate-300' : 'text-slate-600'
                }`}
              >
                {getStateLabel()}
              </motion.p>
            </div>
          </div>

          {/* Micro details positioned relative to viewport bottom */}
          <div className="absolute bottom-10 left-0 right-0 text-center">
            <p className={`text-[9px] font-bold uppercase tracking-[0.3em] ${
              isDarkMode ? 'text-slate-600' : 'text-slate-400'
            }`}>
              WasteMap V2 &bull; Enterprise Stability Suite
            </p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SplashScreen;
